import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import { api } from "../api/client";

const ORG_TYPES = [
    { value: "clinic", label: "Clinic" },
    { value: "hospital", label: "Hospital" },
    { value: "pharmacy", label: "Retail pharmacy" },
    { value: "nursing_home", label: "Nursing home" },
    { value: "diagnostic_lab", label: "Diagnostic lab" },
];

// 2-digit GST state codes (subset of common Indian states)
const STATES = [
    ["27", "Maharashtra"], ["29", "Karnataka"], ["33", "Tamil Nadu"], ["36", "Telangana"],
    ["07", "Delhi"], ["09", "Uttar Pradesh"], ["19", "West Bengal"], ["24", "Gujarat"],
    ["32", "Kerala"], ["08", "Rajasthan"], ["10", "Bihar"], ["06", "Haryana"],
    ["03", "Punjab"], ["28", "Andhra Pradesh"], ["21", "Odisha"], ["23", "Madhya Pradesh"],
    ["05", "Uttarakhand"], ["18", "Assam"], ["20", "Jharkhand"], ["02", "Himachal Pradesh"],
];

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}Z[A-Z0-9]{1}$/;

const Field = ({ label, required, children, error }) => (
    <label className="block">
        <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            {label} {required && <span className="text-rose-500">*</span>}
        </span>
        <div className="mt-1.5">{children}</div>
        {error && <span className="mt-1 block text-xs text-rose-600">{error}</span>}
    </label>
);

const BusinessRegisterContent = () => {
    const navigate = useNavigate();
    const [existing, setExisting] = useState(null);
    const [form, setForm] = useState({
        organization_type: "clinic",
        legal_name: "",
        display_name: "",
        gstin: "",
        drug_license_no: "",
        license_expiry_date: "",
        primary_contact_name: "",
        phone: "",
        has_hospital_certificate: false,
        // address
        line1: "", line2: "", city: "", state: "", state_code: "33", pincode: "",
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        api.get("/b2b/me").then(({ data }) => {
            setExisting(data);
            if (data?.status === "pending_approval") navigate("/business/pending", { replace: true });
            else if (data?.status === "approved") navigate("/business/dashboard", { replace: true });
        }).catch(() => {});
    }, [navigate]);

    const update = (k) => (e) => {
        const v = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
        setForm((f) => ({ ...f, [k]: v }));
    };

    const validate = () => {
        const e = {};
        if (form.legal_name.trim().length < 3) e.legal_name = "Required (≥ 3 chars)";
        if (!GSTIN_REGEX.test(form.gstin)) e.gstin = "Must match GSTIN format (e.g. 33ABCDE1234F1Z5)";
        if (form.gstin && !form.gstin.startsWith(form.state_code)) e.gstin = "GSTIN must start with your state code (" + form.state_code + ")";
        if (!form.drug_license_no.match(/^[A-Z0-9\-/]{6,40}$/i)) e.drug_license_no = "Required";
        if (!form.license_expiry_date) e.license_expiry_date = "Required (YYYY-MM-DD)";
        if (!form.primary_contact_name.trim()) e.primary_contact_name = "Required";
        if (!form.phone.match(/^[+0-9\s\-]{7,20}$/)) e.phone = "Required";
        if (!form.line1.trim() || !form.city.trim() || !form.state.trim()) e.address = "Complete address required";
        if (!form.pincode.match(/^[1-9][0-9]{5}$/)) e.pincode = "6-digit PIN";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async (ev) => {
        ev.preventDefault();
        if (!validate()) {
            toast.error("Please fix the highlighted fields.");
            return;
        }
        try {
            setSubmitting(true);
            await api.post("/b2b/register", {
                organization_type: form.organization_type,
                legal_name: form.legal_name.trim(),
                display_name: (form.display_name || form.legal_name).trim(),
                gstin: form.gstin.toUpperCase(),
                drug_license_no: form.drug_license_no,
                license_expiry_date: form.license_expiry_date,
                primary_contact_name: form.primary_contact_name.trim(),
                phone: form.phone,
                has_hospital_certificate: form.has_hospital_certificate,
                address: {
                    line1: form.line1.trim(),
                    line2: form.line2.trim(),
                    city: form.city.trim(),
                    state: form.state.trim(),
                    state_code: form.state_code,
                    pincode: form.pincode,
                },
            });
            toast.success("Application submitted — we'll review within 24 hours.");
            navigate("/business/pending", { replace: true });
        } catch (err) {
            const d = err?.response?.data?.detail;
            const msg = typeof d === "string" ? d : d?.message || "Submission failed";
            toast.error(msg);
            if (d?.code === "GSTIN_TAKEN") setErrors((e) => ({ ...e, gstin: "Already registered" }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-6 rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft" data-testid="b2b-register-form">
            <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Organisation type" required>
                    <select value={form.organization_type} onChange={update("organization_type")} className="input-clean" data-testid="b2b-reg-org-type">
                        {ORG_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </Field>
                <Field label="Legal name" required error={errors.legal_name}>
                    <input value={form.legal_name} onChange={update("legal_name")} placeholder="ABC Healthcare Pvt Ltd" className="input-clean" data-testid="b2b-reg-legal-name" />
                </Field>
                <Field label="GSTIN" required error={errors.gstin}>
                    <input value={form.gstin} onChange={(e) => setForm((f) => ({ ...f, gstin: e.target.value.toUpperCase() }))} placeholder="33ABCDE1234F1Z5" maxLength={15} className="input-clean font-mono" data-testid="b2b-reg-gstin" />
                </Field>
                <Field label="Drug Licence No." required error={errors.drug_license_no}>
                    <input value={form.drug_license_no} onChange={update("drug_license_no")} placeholder="TN/CH/2024/12345" className="input-clean" data-testid="b2b-reg-dl" />
                </Field>
                <Field label="Licence expiry date" required error={errors.license_expiry_date}>
                    <input type="date" value={form.license_expiry_date} onChange={update("license_expiry_date")} className="input-clean" data-testid="b2b-reg-expiry" />
                </Field>
                <Field label="Primary contact" required error={errors.primary_contact_name}>
                    <input value={form.primary_contact_name} onChange={update("primary_contact_name")} placeholder="Dr. Name / Manager" className="input-clean" data-testid="b2b-reg-contact" />
                </Field>
                <Field label="Phone" required error={errors.phone}>
                    <input value={form.phone} onChange={update("phone")} placeholder="+91 …" className="input-clean" data-testid="b2b-reg-phone" />
                </Field>
                <Field label="Hospital certificate held?">
                    <label className="flex items-center gap-2 pt-2 text-sm text-zinc-700">
                        <input type="checkbox" checked={form.has_hospital_certificate} onChange={update("has_hospital_certificate")} data-testid="b2b-reg-hospcert" />
                        <span>Yes — let us order Rx items without per-order Rx</span>
                    </label>
                </Field>
            </div>

            <hr className="border-zinc-100" />
            <div>
                <p className="label-uppercase mb-2">Address (matches GSTIN registration)</p>
                <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Line 1" required>
                        <input value={form.line1} onChange={update("line1")} className="input-clean" data-testid="b2b-reg-line1" />
                    </Field>
                    <Field label="Line 2 (optional)">
                        <input value={form.line2} onChange={update("line2")} className="input-clean" data-testid="b2b-reg-line2" />
                    </Field>
                    <Field label="City" required>
                        <input value={form.city} onChange={update("city")} className="input-clean" data-testid="b2b-reg-city" />
                    </Field>
                    <Field label="State" required>
                        <select
                            value={form.state_code}
                            onChange={(e) => {
                                const code = e.target.value;
                                const name = STATES.find(([c]) => c === code)?.[1] || "";
                                setForm((f) => ({ ...f, state_code: code, state: name }));
                            }}
                            className="input-clean"
                            data-testid="b2b-reg-state"
                        >
                            {STATES.map(([c, n]) => <option key={c} value={c}>{n} ({c})</option>)}
                        </select>
                    </Field>
                    <Field label="PIN" required error={errors.pincode}>
                        <input value={form.pincode} onChange={update("pincode")} placeholder="600001" maxLength={6} className="input-clean" data-testid="b2b-reg-pincode" />
                    </Field>
                </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-4">
                <p className="text-xs text-zinc-500">By submitting you certify that all licences are valid and current.</p>
                <div className="flex gap-2">
                    <button type="button" onClick={() => navigate("/business")} className="rounded-full border border-zinc-200 px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-50" data-testid="b2b-reg-cancel">
                        Back
                    </button>
                    <button type="submit" disabled={submitting} className="btn-brand !py-2 !px-5 !text-sm" data-testid="b2b-reg-submit">
                        {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                        {submitting ? "Submitting…" : "Submit application"}
                    </button>
                </div>
            </div>
        </form>
    );
};

const BusinessRegister = () => (
    <Layout>
        <section className="mx-auto max-w-3xl px-6 py-10 lg:px-12">
            <button onClick={() => window.history.back()} className="mb-3 inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-brand-700" data-testid="b2b-reg-back">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
            </button>
            <header className="mb-6">
                <p className="label-uppercase">B2B Portal · For Business</p>
                <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900">Register your business</h1>
                <p className="mt-1 text-sm text-zinc-500">Takes 2 minutes. Approval within 24 hours.</p>
            </header>
            <AuthGuard>
                <BusinessRegisterContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default BusinessRegister;
